import { FC, useMemo, useState } from 'react'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { GridColDef } from '@mui/x-data-grid-pro'
import { Colors } from 'src/components/theme'
import { Typography } from '@mui/material'
import styled from '@emotion/styled'
import { DataGrid } from 'src/v2/components/data-grid'
import { PARCEL_TYPES_BY_MCODE_MAP } from 'src/constants/cherre-data-dictionary/zoning-colors'
import { ParcelModal } from 'src/v2/views/owner-reveal/insights/parcels/parcel-modal'
import { ParcelTypeChip } from 'src/v2/components/parcel-type-chip'
import { ParcelChip } from 'src/v2/components/parcel-chip'
import { useAtomValue, useSetAtom } from 'jotai'
import * as atoms from 'src/v2/state/owner-reveal/entity-parcels'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { InsightEmpty } from 'src/views/insight-empty'
import { formatString } from 'src/utils/format-string'

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 196px);
  position: relative;
`

const AssociatedOwnersItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const DEFAULT_COLUMN_SETTINGS = {
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  hideable: false,
  resizable: false,
}

const COLUMNS = (onClickParcel: (id: string) => void): GridColDef[] => {
  return [
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'id',
      headerName: '#',
      width: 20,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ api, row }) => (
        <Typography color={Colors.neutral400} fontSize={14}>
          {api.getRowIndexRelativeToVisibleRows(row.id) + 1}
        </Typography>
      ),
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'name',
      headerName: 'Parcel',
      width: 300,
      renderCell: ({ value, row }) => (
        <ParcelChip maxWidth={280} onClick={() => onClickParcel(row.id)}>
          {formatString(value, 'address')}
        </ParcelChip>
      ),
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'associatedOwners',
      headerName: 'Associated Owners',
      width: 164,
      renderCell: ({ value }) => (
        <AssociatedOwnersItem>
          <Briefcase size={16} color={Colors.neutral300} />
          <Typography color={Colors.neutral400} fontSize={14}>
            {value}
          </Typography>
        </AssociatedOwnersItem>
      ),
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'type',
      headerName: 'Type',
      width: 260,
      renderCell: ({ value }) => {
        return (
          <div>
            <ParcelTypeChip type={value} />
          </div>
        )
      },
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'lotSizeSqFt',
      headerName: 'Lot Size (Sq. Ft.)',
      width: 140,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'lotSizeAcres',
      headerName: 'Lot Size (Acres)',
      width: 140,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'yearBuilt',
      headerName: 'Year Built',
      width: 94,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'numberOfBuildings',
      headerName: '# of Buildings',
      width: 124,
    },

    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'recentAcquisition',
      headerName: 'Recent Acquisition',
      width: 160,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'recentSalePrice',
      headerName: 'Recent Sale Price',
      width: 150,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'mortgageAmount',
      headerName: 'Mortgage Amount',
      width: 160,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'mortgageOriginationDate',
      headerName: 'Mortgage Origination Date',
      width: 230,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'maturityDate',
      headerName: 'Maturity Date',
      width: 130,
    },
    {
      ...DEFAULT_COLUMN_SETTINGS,
      field: 'apn',
      headerName: `Assessor's Parcel Number (APN)`,
      width: 260,
    },
  ]
}

const ROWS_PER_PAGE = 100

const ParcelsTableInner: FC = () => {
  const parcels = useAtomValue(atoms.selectedEntityParcelsOverview)
  const setSelectedParcelId = useSetAtom(atoms.selectedParcelId)
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState(false)
  const isOwnershipMockDataEnabled = isFeatureEnabled('owner-reveal-mock-data')

  const rows = useMemo(() => {
    if (!parcels) {
      return []
    }
    return parcels.map((parcel) => {
      return {
        id: parcel.id,
        name: parcel.address ?? '--',
        associatedOwners: parcel.owners?.length ?? 0,
        type: PARCEL_TYPES_BY_MCODE_MAP[parcel.mCode],
        lotSizeSqFt: parcel.lotSizeAcre,
        lotSizeAcres: parcel.lotSizeAcre,
        yearBuilt: parcel.yearBuilt,
        numberOfBuildings: parcel.numberOfBuildings,
        recentAcquisition: 'XX-XX-20XX',
        recentSalePrice: '$XX,XXX,XXX',
        mortgageAmount: '$XX,XXX,XXX',
        mortgageOriginationDate: 'XX-XX-20XX',
        maturityDate: 'XX-XX-20XX',
        apn: parcel.apn,
      }
    })
  }, [parcels])

  const visibleRows = useMemo(() => {
    return rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE)
  }, [rows, page])

  const count = useMemo(() => Math.ceil(rows.length / ROWS_PER_PAGE), [rows])

  const onClickParcel = (id: string) => {
    setOpen(true)
    setSelectedParcelId(id)
  }

  const onCloseModal = () => {
    setOpen(false)
    setSelectedParcelId(undefined)
  }

  const columns = useMemo(() => {
    if (isOwnershipMockDataEnabled) {
      return COLUMNS(onClickParcel)
    }

    return COLUMNS(onClickParcel).filter(
      (column) =>
        column.field !== 'recentAcquisition' &&
        column.field !== 'recentSalePrice' &&
        column.field !== 'mortgageAmount' &&
        column.field !== 'mortgageOriginationDate' &&
        column.field !== 'maturityDate'
    )
  }, [onClickParcel, isOwnershipMockDataEnabled])
  return (
    <>
      <Container>
        <DataGrid
          disableVirtualization
          columns={columns}
          rows={visibleRows}
          rowHeight={40}
          disableColumnFilter
          disableRowSelectionOnClick
          pagination
          page={page}
          paginationCount={count}
          onPageChange={setPage}
          pinnedColumns={{
            left: ['id', 'name'],
          }}
        />
      </Container>
      <ParcelModal open={open} onClose={onCloseModal} />
    </>
  )
}

export const ParcelsTable: FC = () => {
  const parcels = useAtomValue(atoms.selectedEntityParcelsOverview)

  if (!parcels || !parcels.length) {
    return (
      <InsightEmpty
        message="It looks like we don't have parcel data for this Owner yet. We're constantly gathering new data, so keep an
        eye out for updates."
      />
    )
  }

  return <ParcelsTableInner />
}
